import { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import { Button } from '../core/Button';
import { ConfirmModal } from '../core';
import toast from 'react-hot-toast';
import {
  useEmergencyServiceRates,
  useSetEmergencyServiceRate,
  useDeletePendingEmergencyServiceRate,
} from '../../graphql/hooks/useAdminDashboard';
import type { EmergencyServiceRate } from '../../graphql/types/admin_dashboard';

const STATUS_ORDER: Record<EmergencyServiceRate['status'], number> = {
  PENDING: 0,
  ACTIVE: 1,
  EXPIRED: 2,
};

const STATUS_LABELS: Record<EmergencyServiceRate['status'], string> = {
  PENDING: 'Ausstehend',
  ACTIVE: 'Aktiv',
  EXPIRED: 'Abgelaufen',
};

const STATUS_STYLES: Record<EmergencyServiceRate['status'], string> = {
  PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
  ACTIVE: 'bg-green-50 text-green-700 border border-green-200',
  EXPIRED: 'bg-[#f5f5f5] text-[#6b6b6b] border border-[#e0e0e0]',
};

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const formatDate = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateForApi = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateForDisplay = (date: Date) => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
};

const formatFactor = (value: number) =>
  value.toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const getDaysInMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const getFirstDayOfMonth = (date: Date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  return firstDay === 0 ? 6 : firstDay - 1;
};

export function EmergencyServiceRateView() {
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [weekdayFactor, setWeekdayFactor] = useState('');
  const [specialDayFactor, setSpecialDayFactor] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: rates, isLoading } = useEmergencyServiceRates();
  const { mutate: setEmergencyServiceRateMutation, isPending: isSaving } =
    useSetEmergencyServiceRate();
  const { mutate: deletePendingRate, isPending: isDeleting } =
    useDeletePendingEmergencyServiceRate();

  const toastSuccess = (message: string) =>
    toast.success(message, {
      duration: 3000,
      style: {
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        color: '#15803d', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
    });

  const toastError = (message: string) =>
    toast.error(message, {
      duration: 4000,
      style: {
        background: '#fef2f2', border: '1px solid #fecaca',
        color: '#dc2626', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
    });

  const isValidFloat = (v: string) => /^-?[0-9]*[.,]?[0-9]*$/.test(v);

  const handleFactorChange = (
    v: string,
    setter: (v: string) => void,
    field: string
  ) => {
    if (v === '' || isValidFloat(v)) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
      setter(v);
    } else {
      setFormErrors((prev) => ({ ...prev, [field]: 'Nur Zahlen erlaubt' }));
    }
  };

  const handleFactorBlur = (v: string, setter: (v: string) => void) => {
    if (v.trim() === '') return;
    const num = Number(v.replace(',', '.'));
    if (isNaN(num)) return;
    setter(formatFactor(num));
  };

  const isFutureDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate > today;
  };

  const validate = () => {
    const errors: Record<string, string> = {};

    if (!effectiveDate) {
      errors.effectiveDate = 'Gültig ab Datum ist erforderlich';
    } else if (!isFutureDate(effectiveDate)) {
      errors.effectiveDate = 'Datum muss in der Zukunft liegen';
    }
    if (!weekdayFactor.trim()) errors.weekdayFactor = 'Standardbetrag ist erforderlich';
    if (!specialDayFactor.trim()) errors.specialDayFactor = 'Sonderbetrag ist erforderlich';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setEffectiveDate(null);
    setWeekdayFactor('');
    setSpecialDayFactor('');
    setFormErrors({});
  };

  const getGraphQLErrorMessage = (error: any): string =>
    error?.response?.errors?.[0]?.extensions?.debugMessage ||
    error?.response?.errors?.[0]?.message ||
    'Vorgang fehlgeschlagen';

  const handleSave = () => {
    if (!validate()) {
      setMutationError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }

    setMutationError(null);

    setEmergencyServiceRateMutation(
      {
        effective_date: formatDateForApi(effectiveDate as Date),
        weekday_factor: Number(weekdayFactor.replace(',', '.')),
        special_day_factor: Number(specialDayFactor.replace(',', '.')),
      },
      {
        onSuccess: () => {
          toastSuccess('Notdienstfaktor erfolgreich gespeichert');
          resetForm();
        },
        onError: (error: any) => {
          const message = getGraphQLErrorMessage(error);
          toastError(message);
          setMutationError(message);
        },
      }
    );
  };

  const handleDeletePending = () => {
    deletePendingRate(undefined, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        toastSuccess('Ausstehender Notdienstfaktor gelöscht');
      },
      onError: (error: any) => {
        setDeleteModalOpen(false);
        toastError(getGraphQLErrorMessage(error));
      },
    });
  };

  const previousMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
    );
  };

  const handleDateClick = (date: Date) => {
    setEffectiveDate(date);
    setFormErrors((prev) => ({ ...prev, effectiveDate: '' }));
    setShowCalendar(false);
  };

  const isSelectedDate = (date: Date): boolean =>
    !!effectiveDate &&
    date.getDate() === effectiveDate.getDate() &&
    date.getMonth() === effectiveDate.getMonth() &&
    date.getFullYear() === effectiveDate.getFullYear();

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarMonth);

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <button onClick={previousMonth} type="button">
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>

          <div className="text-sm font-medium">
            {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </div>

          <button onClick={nextMonth} type="button">
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
        </div>

        <div className="grid grid-cols-7 text-center text-xs text-[#6b6b6b] mb-2">
          {dayNames.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const date = new Date(
              calendarMonth.getFullYear(),
              calendarMonth.getMonth(),
              i + 1
            );

            const selected = isSelectedDate(date);
            const todayDate = isToday(date);
            const disabled = !isFutureDate(date);

            return (
              <button
                key={i}
                type="button"
                onClick={() => !disabled && handleDateClick(date)}
                disabled={disabled}
                className={`h-9 rounded-md text-sm transition-colors ${
                  disabled
                    ? 'text-[#d1d5db] cursor-not-allowed'
                    : selected
                      ? 'bg-[#0084c7] text-white'
                      : todayDate
                        ? 'bg-[#0084c7]/10 text-[#0084c7] font-medium'
                      : 'hover:bg-[#f5f5f5]'
                }`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const sortedRates = [...(rates ?? [])].sort((a, b) => {
    const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;
    return (
      new Date(b.effective_date).getTime() - new Date(a.effective_date).getTime()
    );
  });

  return (
    <div className="space-y-8">
      {/* Form */}
      <div className="max-w-[600px] space-y-6">
        <div>
          <h2 className="text-[#1c1c1c] text-[18px] font-medium mb-1">
            Neuen Notdienstfaktor anlegen
          </h2>
        </div>

        {mutationError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-[14px]">
            {mutationError}
          </div>
        )}

        <div className="space-y-4">
          <div className="relative">
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              Gültig ab
            </label>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={
                  effectiveDate
                    ? formatDateForDisplay(effectiveDate)
                    : 'Datum auswählen'
                }
                onClick={() => {
                  if (effectiveDate) setCalendarMonth(effectiveDate);
                  setShowCalendar((prev) => !prev);
                }}
                className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
              />
              <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />
            </div>

            {showCalendar && (
              <div className="absolute z-30 mt-2 w-full box-border md:w-[320px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4">
                {renderCalendar()}
              </div>
            )}

            {formErrors.effectiveDate && (
              <div className="text-red-500 text-[12px] mt-1">
                {formErrors.effectiveDate}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                Standardbetrag
              </label>
              <input
                type="text"
                value={weekdayFactor}
                onChange={(e) =>
                  handleFactorChange(e.target.value, setWeekdayFactor, 'weekdayFactor')
                }
                onBlur={() => handleFactorBlur(weekdayFactor, setWeekdayFactor)}
                className="w-full box-border h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
              />
              {formErrors.weekdayFactor && (
                <div className="text-red-500 text-[12px] mt-1">
                  {formErrors.weekdayFactor}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                Sonderbetrag
              </label>
              <input
                type="text"
                value={specialDayFactor}
                onChange={(e) =>
                  handleFactorChange(e.target.value, setSpecialDayFactor, 'specialDayFactor')
                }
                onBlur={() => handleFactorBlur(specialDayFactor, setSpecialDayFactor)}
                className="w-full box-border h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
              />
              {formErrors.specialDayFactor && (
                <div className="text-red-500 text-[12px] mt-1">
                  {formErrors.specialDayFactor}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      {/* History table */}
      <div>
        <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-4">
          Verlauf
        </h3>

        {isLoading ? (
          <p className="text-[#6b6b6b] text-[14px]">Lädt...</p>
        ) : sortedRates.length === 0 ? (
          <p className="text-[#6b6b6b] text-[14px]">
            Noch keine Notdienstfaktoren hinterlegt.
          </p>
        ) : (
          <div className="space-y-2">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
              <div>Gültig ab</div>
              <div>Standardbetrag</div>
              <div>Sonderbetrag</div>
              <div>Status</div>
              <div className="w-[40px]" />
            </div>

            {/* Table Rows */}
            {sortedRates.map((rate) => (
              <div
                key={`${rate.effective_date}-${rate.status}`}
                className="grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors rounded-lg"
              >
                <div className="text-[#1c1c1c]">
                  {formatDate(rate.effective_date)}
                </div>
                <div className="text-[#6b6b6b]">
                  {formatFactor(rate.weekday_factor)}
                </div>
                <div className="text-[#6b6b6b]">
                  {formatFactor(rate.special_day_factor)}
                </div>
                <div>
                  <span
                    className={`inline-block px-2.5 py-1 rounded-full text-[12px] font-medium ${STATUS_STYLES[rate.status]}`}
                  >
                    {STATUS_LABELS[rate.status]}
                  </span>
                </div>
                <div className="w-[40px]">
                  {rate.status === 'PENDING' && (
                    <button
                      onClick={() => setDeleteModalOpen(true)}
                      disabled={isDeleting}
                      className="w-8 h-8 flex items-center justify-center hover:bg-[#fef2f2] rounded disabled:opacity-50"
                    >
                      <Trash2 className="size-4 text-[#ef4444]" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Ausstehenden Notdienstfaktor löschen"
        message="Möchten Sie den ausstehenden, noch nicht wirksamen Notdienstfaktor wirklich löschen?"
        onConfirm={handleDeletePending}
      />
    </div>
  );
}